/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import cn from 'classnames'

import ReprocessingOverlay from '../../pages/Schedule/ReprocessingOverlay'
import { Flex } from '../Layout'
import List from '../List'
import GameListFilters, {
  SortOrderButton,
} from '../List/GameList/GameListFilters'
import useGetScheduleFiltering from '../List/useGetScheduleFiltering'
import Picture from '../Picture'
import { FIRST_CELL_WIDTH } from '../List/list-constants'
import { media } from '../Responsive'
import ToggleSection from 'src/pages/Schedule/EditRegistration/ToggleSection'

const transformArray = (array) => {
  let result = []
  let seen = new Set()
  array.forEach((item) => {
    item.stats.forEach((stat) => {
      if (!seen.has(stat.abbr)) {
        result.push({
          text: stat.abbr,
          toolTip: stat.name,
        })
        seen.add(stat.abbr)
      }
    })
  })
  return result
}

export const iceHockeySkaterStats = [
  {
    bigText: 'Games Played',
    text: 'GP',
    accessor: 'GP',
    toolTip: 'Games Played',
  },
  {
    bigText: 'Goals',
    text: 'G',
    accessor: 'G',
    toolTip: 'Goals',
  },
  {
    bigText: 'Assists',
    text: 'A',
    accessor: 'A',
    toolTip: 'Assists',
  },
  {
    bigText: 'Points',
    text: 'P',
    accessor: 'P',
    toolTip: 'Points',
  },
  {
    text: 'PiM',
    accessor: 'PiM',
    toolTip: 'Penalty Minutes',
  },
  {
    text: 'PPG',
    accessor: 'PPG',
    toolTip: 'Power Play Goals',
  },
  {
    text: 'PTS/G',
    accessor: 'PTS/G',
    toolTip: 'Average Points Per Game',
  },
]

export const soccerSkaterStats = [
  {
    bigText: 'MP',
    text: 'MP',
    accessor: 'MP',
    toolTip: 'Matches Played',
  },
  {
    bigText: 'G',
    text: 'G',
    accessor: 'G',
    toolTip: 'Goals',
  },
  {
    bigText: 'A',
    text: 'A',
    accessor: 'A',
    toolTip: 'Assists',
  },
  {
    text: 'GFA',
    accessor: 'GFA',
    toolTip: 'Goals For Average',
  },
  {
    text: 'GC', // YC
    accessor: 'GC',
    toolTip: 'Goals Conceded', // Yellow Cards
  },
  {
    text: 'GCA', // YC
    accessor: 'GCA',
    toolTip: 'Goals Conceded Average', // Yellow Cards
  },
  {
    text: 'SV', // YC
    accessor: 'SV',
    toolTip: 'Saves', // Yellow Cards
  },
  {
    text: 'FC',
    toolTip: 'Fouls Committed',
  },
  {
    text: 'YC', // YC
    accessor: 'YC',
    toolTip: 'Yellow Cards', // Yellow Cards
  },
  {
    text: 'RC', // RC
    accessor: 'RC',
    toolTip: 'Red Cards', // Red Cards
  },
]

export const iceHockeyGoalieStats = [
  {
    text: 'GP',
    toolTip: 'Games Played',
  },
  {
    text: 'W',
    toolTip: 'Wins',
  },
  {
    text: 'L',
    toolTip: 'Losses',
  },
  {
    text: 'T',
    toolTip: 'Ties',
  },
  {
    text: 'OTL',
    toolTip: 'Overtime Losses',
  },
  {
    text: 'GAA',
    toolTip: 'Goals Against Average',
  },
  {
    text: 'SV%',
    toolTip: 'Save Percentage',
  },
  {
    text: 'SO',
    toolTip: 'Shutouts',
  },
  {
    text: 'Min',
    accessor: 'MP',
    toolTip: 'Minutes Played',
  },
  {
    text: 'PiM',
    toolTip: 'Penalty Minutes',
  },
  {
    text: 'PPG',
    accessor: 'PPG',
    toolTip: 'Power Play Goals',
  },
  {
    text: 'SA',
    toolTip: 'Shots Against',
  },
  {
    text: 'SV',
    toolTip: 'Saves',
  },
  {
    text: 'GA',
    toolTip: 'Goals Against',
  },
]

export const soccerGoalieStats = [
  {
    bigText: 'MP',
    text: 'MP',
    accessor: 'MP',
    toolTip: 'Matches Played',
  },
  {
    bigText: 'G',
    text: 'G',
    accessor: 'G',
    toolTip: 'Goals',
  },
  {
    bigText: 'A',
    text: 'A',
    accessor: 'A',
    toolTip: 'Assists',
  },
  {
    text: 'GFA',
    accessor: 'GFA',
    toolTip: 'Goals For Average',
  },
  {
    text: 'GC', // YC
    accessor: 'GC',
    toolTip: 'Goals Conceded', // Yellow Cards
  },
  {
    text: 'GCA', // YC
    accessor: 'GCA',
    toolTip: 'Goals Conceded Average', // Yellow Cards
  },
  {
    text: 'SV', // YC
    accessor: 'SV',
    toolTip: 'Saves', // Yellow Cards
  },
  {
    text: 'FC',
    toolTip: 'Fouls Committed',
  },
  {
    text: 'YC', // YC
    accessor: 'YC',
    toolTip: 'Yellow Cards', // Yellow Cards
  },
  {
    text: 'RC', // RC
    accessor: 'RC',
    toolTip: 'Red Cards', // Red Cards
  },
]

const Statistics = ({
  autoSelectMostRecentComp,
  areFiltersHidden,
  entityType,
  entityId,
  defaultSort,
  useTeamColumn,
  refreshKey,
  scheduleReprocessingListener,
  isSoccer = false,
}) => {
  const abortControllerRef = useRef(null)

  const skaterStats = useMemo(() => {
    return isSoccer ? soccerSkaterStats : iceHockeySkaterStats
  }, [isSoccer])

  const goalieStats = useMemo(() => {
    return isSoccer ? soccerGoalieStats : iceHockeyGoalieStats
  }, [isSoccer])

  const BASE_SLUG = `/${entityType}/${entityId}/stats/players`

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [sortBy, setSortBy] = useState(defaultSort)
  const [direction, setDirection] = useState('desc')
  const [items, setItems] = useState([])
  const [isGoalieStats, setIsGoalieStats] = useState(false)
  const [pagination, setPagination] = useState({})
  const [slug, setSlug] = useState(BASE_SLUG)
  const [isGlobalFilterEnabled, setIsGlobalFilterEnabled] = useState(true)
  const [scheduleForSearch, setScheduleForSearch] = useState(null)

  const isSchedule = entityType === ENTITY_TYPES.schedule
  const isTeam = entityType === ENTITY_TYPES.team
  const args = {}

  if (isSchedule) {
    args.schedule_id = entityId
  } else if (isTeam) {
    args.team_id = entityId
  }

  const onTeamChange = (teamId) => {
    setTeam(teamId)
    if (teamId) {
      setSlug(`/schedules/${entityId}/stats/players/team/${teamId}`)
    } else {
      setSlug(BASE_SLUG)
    }
  }

  const onScheduleChange = useCallback(
    (scheduleId) => {
      if (scheduleId) {
        setScheduleForSearch(scheduleId)
      } else {
        setSlug(BASE_SLUG)
        setScheduleForSearch(null)
      }
    },
    [isSchedule, entityId, setSlug]
  )

  const onIsGoalie = (isGoalie) => {
    setIsGoalieStats(isGoalie)
  }

  const {
    teams,
    team,
    setTeam,
    filters,
    state,
    setState,
    loaded,
    setSearchTerm,
    searchTerm,
    debouncedSearchTerm,
  } = useGetScheduleFiltering(args, {
    defaultSort: 'desc',
    autoSelectMostRecentComp,
    onTeamChange,
    onScheduleChange,
    onIsGoalie,
  })

  const getItems = async (page) => {
    setLoading(true)
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()
      const params = {
        page,
        sortBy,
        sort: direction,
        goalie: isGoalieStats ? 1 : 0,
        ...(scheduleForSearch && { division_id: scheduleForSearch }),
        ...(isGlobalFilterEnabled && { global: 1 }),
        ...(debouncedSearchTerm && { player_name: debouncedSearchTerm }),
      }
      const response = await req(slug, {
        query: params,
        signal: abortControllerRef.current.signal, // Step 3: Pass the signal to the fetch request
      })

      const myData = transformArray(response.data)
      console.log(myData)

      setItems(response.data)
      setPagination(response.meta?.pagination)
    } catch (e) {
      if (e.name === 'AbortError') {
        console.log('Fetch aborted')
      } else {
        setError(e.message)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (loaded) {
      getItems()
    }
  }, [
    loaded,
    direction,
    slug,
    entityId,
    sortBy,
    isGoalieStats,
    refreshKey,
    isGlobalFilterEnabled,
    scheduleForSearch,
    debouncedSearchTerm,
  ])

  const headers = [
    {
      text: '#',
      sizePercent: 5,
      sort: 'player.player_number',
      Cell: ({ item, isSorted }) => {
        return (
          <Flex
            className='player-statistics-player'
            alignItems='center'
            css={css`
              ${isSorted && `color: ${colors.DEFAULT_FLAIR};`}
            `}
          >
            {item?.player?.player_number || '#'}
          </Flex>
        )
      },
    },
    {
      text: t('common:player'),
      sizePercent: useTeamColumn ? 25 : 34,
      sort: 'player.full_name',
      Cell: ({ item, isSorted }) => {
        let link
        if (item?.player?.id) {
          link = `${ROUTES.PLAYER_ROOT}/${item.player.id}`
        }

        return (
          <Flex
            className='player-statistics-player'
            alignItems='center'
            as={link ? Link : undefined}
            to={link ? link : undefined}
            css={css`
              ${isSorted && `color: ${colors.DEFAULT_FLAIR};`}
            `}
          >
            <Picture
              size='xxsmall'
              imageId={getImageThumbnailId(item?.player)}
              iconName='user'
              css={css`
                margin-right: 8px;
              `}
            />
            {item?.player?.name_first} {item?.player?.name_last}
          </Flex>
        )
      },
    },
    useTeamColumn && {
      text: 'Team',
      sizePercent: 25,
      sort: 'team.name',
      minWidth: FIRST_CELL_WIDTH,
      Cell: ({ item, isSorted }) => {
        let link
        if (item?.team?.id) {
          link = `${ROUTES.TEAM_ROOT}/${item.team.id}`
        }

        return (
          <Flex
            alignItems='center'
            as={link ? Link : undefined}
            to={link ? link : undefined}
            css={css`
              font-size: 14px;
              ${isSorted && `color: ${colors.DEFAULT_FLAIR};`}
            `}
          >
            <Picture
              square
              fit
              size='xxsmall'
              imageId={getImageThumbnailId(item?.team)}
              iconName='user-friends'
              css={css`
                margin-right: 8px;
              `}
            />
            {item?.team?.name_full}
          </Flex>
        )
      },
    },
    ...(isGoalieStats ? goalieStats : skaterStats).map((stat) => {
      const accessor = stat.accessor || stat.text
      return {
        text: stat.text,
        sizePercent: 10,
        sort: accessor,
        fontSize: 14,
        minWidth: 50,
        toolTip: stat.toolTip,
        accessor: (item) => {
          return `${item.stats.find((s) => s.abbr === accessor)?.value || 0}`
        },
        Header: () => {
          return (
            <div
              css={css`
                .mobile {
                  display: none;
                }

                ${media.mobile} {
                  .desktop {
                    display: none;
                  }

                  .mobile {
                    display: block;
                  }
                }
              `}
            >
              <div className='desktop'>{stat.bigText || stat.text}</div>
              <div className='mobile'>{stat.text}</div>
            </div>
          )
        },
      }
    }),
  ]

  return (
    <>
      <ReprocessingOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
      />
      <div
        css={css`
          margin-top: 24px;

          ${scheduleReprocessingListener &&
          css`
            opacity: 0.7;
            filter: blur(2px);
            pointer-events: none;
          `}
        `}
      >
        <GameListFilters
          filters={filters}
          team_id={entityId}
          setSlug={setSlug}
          state={state}
          setState={setState}
          loading={loading}
          teams={teams}
          team={team}
          onTeamChange={onTeamChange}
          onScheduleChange={onScheduleChange}
          isHidden={areFiltersHidden}
        >
          <Flex column noFlex>
            <label
              htmlFor={isGoalieStats ? 'skater-stats' : 'goalie-stats'}
              css={css`
                height: 12px;
                font-size: 12px;
                line-height: 12px;
                color: ${loading && 'rgba(255, 255, 255, 0.7)'};
              `}
            >
              Player Type
            </label>
            <Flex
              css={css`
                margin-top: 8px;
                padding: 0 16px 16px 0;
              `}
            >
              <SortOrderButton
                id='skater-stats'
                disabled={loading}
                type='button'
                className={cn({ sorted: !isGoalieStats })}
                onClick={() => {
                  setIsGoalieStats(false)
                  setDirection('desc')
                  setSortBy('P')
                }}
              >
                {isSoccer ? 'Player' : 'Skater'}
              </SortOrderButton>
              <SortOrderButton
                id='goalie-stats'
                disabled={loading}
                type='button'
                className={cn({ sorted: isGoalieStats })}
                onClick={() => {
                  setIsGoalieStats(true)
                  setDirection('asc')
                  setSortBy('GAA')
                }}
                css={css`
                  margin-left: 8px;
                `}
              >
                {isSoccer ? 'Keeper' : 'Goalie'}
              </SortOrderButton>
            </Flex>
          </Flex>
          {isSchedule && scheduleForSearch ? (
            <Flex column noFlex>
              <label
                htmlFor={isGoalieStats ? 'skater-stats' : 'goalie-stats'}
                css={css`
                  height: 12px;
                  font-size: 12px;
                  line-height: 12px;
                  color: ${loading && 'rgba(255, 255, 255, 0.7)'};
                `}
              >
                Stats Filter
              </label>
              <Flex
                css={css`
                  margin-top: 8px;
                  padding: 0 16px 16px 0;
                `}
              >
                <SortOrderButton
                  id='stats-filter-global'
                  disabled={loading}
                  type='button'
                  className={cn({ sorted: isGlobalFilterEnabled })}
                  onClick={() => {
                    setIsGlobalFilterEnabled(true)
                    setSlug(BASE_SLUG)
                  }}
                >
                  {'Season'}
                </SortOrderButton>
                <SortOrderButton
                  id='stats-filter-division'
                  disabled={loading}
                  type='button'
                  className={cn({ sorted: !isGlobalFilterEnabled })}
                  onClick={() => {
                    setIsGlobalFilterEnabled(false)
                    if (scheduleForSearch && isSchedule) {
                      setSlug(`/schedules/${scheduleForSearch}/stats/players`)
                    }
                  }}
                  css={css`
                    margin-left: 8px;
                  `}
                >
                  {'Division'}
                </SortOrderButton>
              </Flex>
            </Flex>
          ) : null}
        </GameListFilters>
        {error ? (
          <div
            css={css`
              margin-top: 100px;
              text-align: center;
            `}
          >
            {error}
          </div>
        ) : (
          <List
            showSearch={!isTeam}
            headers={headers.filter((h) => typeof h !== 'undefined')}
            fullNoItemsText='There are no player statistics available yet'
            shouldStickyHeader
            highlightSorted
            listProps={{
              items,
              loading,
              setDirection,
              setSort: setSortBy,
              sort: sortBy,
              direction,
              pagination,
              getItems,
              getKey: (item) => `${item?.player?.id}-${item?.team?.id}`,
              setSearchTerm,
              searchTerm,
            }}
          />
        )}
      </div>
    </>
  )
}

export default Statistics
